import { Link } from 'react-router-dom';
import LegalDocumentLayout from '@/app/Legal/LegalDocumentLayout';

const TermsOfServicePage = () => (
  <LegalDocumentLayout
    title="Terms of Service"
    description="Terms governing access to DwellBot Games and PlayClaw.Live, using the current terms content from the DwellBot website."
  >
    <p>
      <strong>Effective date:</strong> February 19, 2026
      <br />
      <strong>Last updated:</strong> February 19, 2026
    </p>

    <p>
      These Terms of Service (&quot;<strong>Terms</strong>&quot;) govern your access to and use of the websites,
      apps, and related services provided by <strong>Dwell Bot Inc.</strong> (doing business as{' '}
      <strong>DwellBot Games</strong>) (&quot;<strong>DwellBot</strong>,&quot; &quot;<strong>we</strong>,&quot;{' '}
      &quot;<strong>us</strong>,&quot; &quot;<strong>our</strong>&quot;), including{' '}
      <strong>DwellBot Games</strong> and <strong>PlayClaw.Live</strong> (collectively, the{' '}
      &quot;<strong>Services</strong>&quot;).
    </p>

    <p>By accessing or using the Services, you agree to these Terms. If you do not agree, do not use the Services.</p>

    <p>
      If you have questions, contact us at <a href="mailto:support@dwell.bot">support@dwell.bot</a> or{' '}
      <a href="mailto:privacy@dwell.bot">privacy@dwell.bot</a>.
    </p>

    <hr />

    <h2>1) Eligibility and account registration</h2>

    <h3>1.1 Eligibility</h3>
    <p>
      You must be at least <strong>13 years old</strong> to use the Services. If you are under the age of majority
      where you live, you may use the Services only with the consent of a parent or legal guardian who agrees to these
      Terms.
    </p>

    <h3>1.2 Account creation</h3>
    <p>
      Some features require an account. You agree to provide accurate information and keep it up to date. You are
      responsible for maintaining the confidentiality of your login credentials and for all activity under your
      account.
    </p>

    <h3>1.3 Social sign-in</h3>
    <p>
      We may allow you to sign in using third-party authentication providers (e.g., Google or Facebook) via{' '}
      <strong>Clerk</strong>. Your use of those providers is also governed by their own terms and policies.
    </p>

    <hr />

    <h2>2) The Services (what we provide)</h2>
    <p>
      The Services may include interactive experiences, such as real-time gameplay, livestream-style viewing, and
      other features. Some experiences may involve the operation of physical devices (for example, remote-controlled
      game machines).
    </p>
    <p>
      We may update, modify, or discontinue parts of the Services at any time. We do not guarantee that any feature
      will always be available.
    </p>

    <hr />

    <h2>3) Acceptable use</h2>
    <p>You agree not to misuse the Services. For example, you will not:</p>
    <ul>
      <li>Break the law or encourage illegal activity</li>
      <li>Attempt to access accounts, systems, or data you are not authorized to access</li>
      <li>Interfere with or disrupt the Services (including by probing, scanning, or testing vulnerabilities)</li>
      <li>Use bots/scripts to manipulate queues, game outcomes, or availability</li>
      <li>Cheat, exploit bugs, or attempt to reverse engineer protected aspects of the Services</li>
      <li>Harass, threaten, defame, or abuse others</li>
      <li>Upload or transmit malware or harmful code</li>
      <li>Use the Services to transmit unlawful, infringing, or obscene content</li>
      <li>Attempt to bypass or defeat security or access controls</li>
    </ul>
    <p>
      We may investigate violations and take action, including removing content, suspending access, or terminating
      accounts.
    </p>

    <hr />

    <h2>4) Real-time sessions, safety, and fairness</h2>

    <h3>4.1 Session quality and interruptions</h3>
    <p>Real-time experiences can be affected by network conditions, device performance, outages, and other factors. You acknowledge that:</p>
    <ul>
      <li>Sessions may be interrupted, delayed, or degraded</li>
      <li>We do not guarantee uninterrupted access or perfect responsiveness</li>
    </ul>

    <h3>4.2 Fair play, queues, and rate limits</h3>
    <p>
      We may use queues, rate limits, time limits, and anti-abuse controls to keep experiences fair and reliable. You
      agree not to attempt to circumvent these controls.
    </p>

    <h3>4.3 Safety and device control limits</h3>
    <p>If an experience involves remote control of physical devices, you agree:</p>
    <ul>
      <li>To follow any on-screen instructions and safety prompts</li>
      <li>Not to attempt to cause damage or unsafe behavior</li>
      <li>
        That we may impose automated or manual safety limits (including pausing, stopping, or overriding control) to
        protect people, property, and equipment
      </li>
    </ul>

    <hr />

    <h2>5) User content</h2>

    <h3>5.1 Your content</h3>
    <p>
      &quot;<strong>User Content</strong>&quot; means any content you submit, upload, stream, or otherwise make
      available through the Services (e.g., chat messages, photos, profile images, clips you upload).
    </p>
    <p>You retain ownership of your User Content, but you grant DwellBot a license to use it as described below.</p>

    <h3>5.2 License to DwellBot</h3>
    <p>
      To operate the Services, you grant DwellBot a worldwide, non-exclusive, royalty-free, sublicensable, and
      transferable license to host, store, reproduce, modify (for technical purposes, e.g., formatting), transmit,
      display, perform, and distribute your User Content <strong>solely</strong>:
    </p>
    <ul>
      <li>To provide and maintain the Services</li>
      <li>To improve reliability, prevent abuse, and enforce policies</li>
      <li>To comply with legal obligations</li>
      <li>To create aggregated or anonymized analytics</li>
    </ul>

    <p>This license ends when you delete the content or your account, except where:</p>
    <ul>
      <li>Content has been shared with others and they have not deleted it</li>
      <li>We must retain it for legal, security, or fraud-prevention reasons</li>
      <li>It exists in backups for a limited time</li>
    </ul>

    <h3>5.3 Your responsibilities</h3>
    <p>
      You represent that you have all rights necessary to submit User Content and that it does not violate law or
      third-party rights. You are responsible for your User Content.
    </p>

    <h3>5.4 Moderation</h3>
    <p>
      We may (but are not obligated to) monitor, review, or remove User Content. We may remove content and suspend
      accounts for any reason, including policy violations.
    </p>

    <hr />

    <h2>6) Intellectual property</h2>

    <h3>6.1 Our IP</h3>
    <p>
      The Services, including all software, designs, text, graphics, logos, and other content provided by DwellBot
      (excluding User Content) are owned by DwellBot or its licensors and are protected by applicable laws.
    </p>

    <h3>6.2 Limited license to you</h3>
    <p>
      We grant you a limited, revocable, non-exclusive, non-transferable license to use the Services for your
      personal, non-commercial use, subject to these Terms.
    </p>

    <h3>6.3 Restrictions</h3>
    <p>
      You may not copy, modify, distribute, sell, lease, reverse engineer, or attempt to extract source code from the
      Services except to the extent permitted by law.
    </p>

    <hr />

    <h2>7) Purchases, subscriptions, and virtual items (if applicable)</h2>
    <p>
      If the Services offer paid features (e.g., credits, subscriptions, premium queue access, or in-game items):
    </p>
    <ul>
      <li>
        <strong>Pricing and taxes:</strong> Prices may change. Taxes may apply.
      </li>
      <li>
        <strong>Billing:</strong> Payments are processed by third-party processors. You agree to their terms as well.
      </li>
      <li>
        <strong>No guarantee of availability:</strong> Paid access may still be affected by outages or operational
        constraints.
      </li>
      <li>
        <strong>Refunds:</strong> Unless required by law, purchases are generally <strong>non-refundable</strong>. If
        you believe a charge is incorrect, contact <a href="mailto:support@dwell.bot">support@dwell.bot</a> promptly.
      </li>
    </ul>

    <blockquote>
      <p>
        If you already have a refund policy, paste it here and match your actual practice (this is important).
      </p>
    </blockquote>

    <hr />

    <h2>8) Third-party services and links</h2>
    <p>
      The Services may integrate with third-party services (e.g., authentication providers, embedded media,
      analytics). DwellBot is not responsible for third-party services, and your use of them may be governed by their
      terms and policies.
    </p>

    <hr />

    <h2>9) Privacy</h2>
    <p>
      Our <Link to="/privacy-policy">Privacy Policy</Link> explains how we collect and use information. By using the
      Services, you agree to our Privacy Policy.
    </p>

    <hr />

    <h2>10) Termination</h2>

    <h3>10.1 By you</h3>
    <p>
      You may stop using the Services at any time. You may request account deletion by contacting{' '}
      <a href="mailto:privacy@dwell.bot">privacy@dwell.bot</a>.
    </p>

    <h3>10.2 By us</h3>
    <p>We may suspend or terminate your access to the Services at any time if we reasonably believe:</p>
    <ul>
      <li>You violated these Terms or our policies</li>
      <li>Your use poses a security, safety, or legal risk</li>
      <li>Your use harms other users or the Services</li>
    </ul>
    <p>We may also terminate or discontinue the Services at any time.</p>

    <h3>10.3 Effect of termination</h3>
    <p>
      Upon termination, your right to use the Services ends. Sections that by their nature should survive termination
      will survive (e.g., IP, disclaimers, limitation of liability, dispute terms).
    </p>

    <hr />

    <h2>11) Disclaimers</h2>
    <p>
      THE SERVICES ARE PROVIDED <strong>&quot;AS IS&quot;</strong> AND <strong>&quot;AS AVAILABLE.&quot;</strong> TO
      THE MAXIMUM EXTENT PERMITTED BY LAW, DWELLBOT DISCLAIMS ALL WARRANTIES, EXPRESS OR IMPLIED, INCLUDING IMPLIED
      WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
    </p>
    <p>WE DO NOT WARRANT THAT THE SERVICES WILL BE UNINTERRUPTED, ERROR-FREE, OR SECURE.</p>

    <hr />

    <h2>12) Limitation of liability</h2>
    <p>
      TO THE MAXIMUM EXTENT PERMITTED BY LAW, DWELLBOT WILL NOT BE LIABLE FOR INDIRECT, INCIDENTAL, SPECIAL,
      CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS, REVENUE, DATA, OR GOODWILL, ARISING OUT OF OR
      RELATING TO YOUR USE OF (OR INABILITY TO USE) THE SERVICES.
    </p>
    <p>
      TO THE MAXIMUM EXTENT PERMITTED BY LAW, DWELLBOT&apos;S TOTAL LIABILITY FOR ANY CLAIM ARISING OUT OF OR RELATING
      TO THE SERVICES WILL NOT EXCEED THE GREATER OF:
    </p>
    <ul>
      <li>
        <strong>$100</strong>, OR
      </li>
      <li>
        THE AMOUNT YOU PAID DWELLBOT (IF ANY) FOR THE SERVICES IN THE <strong>12 MONTHS</strong> BEFORE THE EVENT
        GIVING RISE TO THE CLAIM.
      </li>
    </ul>
    <p>Some jurisdictions do not allow certain limitations, so some of the above may not apply to you.</p>

    <hr />

    <h2>13) Indemnification</h2>
    <p>
      You agree to indemnify and hold harmless DwellBot and its officers, directors, employees, and agents from any
      claims, liabilities, damages, losses, and expenses (including reasonable attorneys&apos; fees) arising out of or
      related to:
    </p>
    <ul>
      <li>Your use of the Services</li>
      <li>Your User Content</li>
      <li>Your violation of these Terms</li>
      <li>Your violation of any law or third-party right</li>
    </ul>

    <hr />

    <h2>14) Dispute resolution, governing law, and venue</h2>

    <h3>14.1 Governing law</h3>
    <p>
      These Terms are governed by the laws of the <strong>State of Delaware</strong>, excluding its conflict of laws
      rules (unless your local consumer protection laws require otherwise).
    </p>

    <h3>14.2 Venue</h3>
    <p>
      Unless otherwise required by law, you agree that disputes will be brought in the state or federal courts located
      in <strong>Delaware</strong>, and you consent to personal jurisdiction there.
    </p>

    <blockquote>
      <p>
        Optional: If you want arbitration (and you are prepared to implement it properly), add an arbitration clause
        here. If you do not already have a legal preference, it is fine to omit.
      </p>
    </blockquote>

    <hr />

    <h2>15) Changes to these Terms</h2>
    <p>
      We may update these Terms from time to time. We will post the updated Terms and revise the &quot;Last
      updated&quot; date above. If changes are material, we will provide additional notice as required by law.
    </p>

    <hr />

    <h2>16) Contact</h2>
    <p>
      <strong>Dwell Bot Inc. (DBA DwellBot Games)</strong>
      <br />
      <strong>Email:</strong> <a href="mailto:support@dwell.bot">support@dwell.bot</a> (support)
      <br />
      <strong>Email:</strong> <a href="mailto:privacy@dwell.bot">privacy@dwell.bot</a> (privacy)
      <br />
      <strong>Mailing address:</strong> 1333 North Adams Street, Tallahassee, FL 32303
    </p>
  </LegalDocumentLayout>
);

export default TermsOfServicePage;
